import { useCallback, useEffect, useMemo, useState } from 'react';
import { FullScreenModal } from 'components/Signature/styles';
import { Spin } from 'antd';
import { selectAuthUser } from 'store/user/shared/selectors';
import { useSelector } from 'react-redux';
import { ColumnsType } from 'antd/lib/table';
import {
  ReviewerCommentState,
  ScoreState
} from 'views/GivePointsInitiative/pages/ScoreAssignedInitiatives/components/ScoreInitiative/type';
import {
  extractScoreInitiativeDataViewFile,
  getTotalScore,
  initiativeComment
} from 'views/GivePointsInitiative/pages/ScoreAssignedInitiatives/components/ScoreInitiative/service';
import styled from 'styled-components';
import ModalGenerate from './ModelGenerate';
import { StyledCheckbox } from './style';
import ModalViewPDFSignExport from './ModalViewPDFSignExport';

const StyledConfirm = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 20px;
`;

interface ModalGenerateAndOpenForSignProps {
  open: boolean;
  onClose: () => void;
  initiative: any;
  cvCertificate: any;
  isCv1: boolean;
}

const ModalGenerateAndOpenForSign = ({
  open,
  onClose,
  initiative,
  cvCertificate,
  isCv1
}: ModalGenerateAndOpenForSignProps) => {
  const [generatedFile, setGeneratedFile] = useState<File | null>(null);
  const [openGenerate, setOpenGenerate] = useState(true);
  const [openSignFile, setOpenSignFile] = useState(false);

  const [loading, setLoading] = useState(true);

  const [scores, setScores] = useState<ScoreState>(
    () => extractScoreInitiativeDataViewFile(cvCertificate).scores
  );

  const [reviewerComment, setReviewerComment] = useState<ReviewerCommentState>(
    () => extractScoreInitiativeDataViewFile(cvCertificate).reviewerComment
  );

  const [confirm, setConfirm] = useState();

  useEffect(() => {
    const { scores, reviewerComment } = extractScoreInitiativeDataViewFile(cvCertificate);
    setScores(scores);
    setReviewerComment(reviewerComment);
    setConfirm(isCv1 ? initiative.is_cv1_confirmed : initiative.is_cv2_confirmed);
  }, [initiative, isCv1]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 10);
    return () => clearTimeout(timer);
  }, []);

  const calculatedScore = useMemo(() => {
    const a_total_score = getTotalScore([scores.a1_score, scores.a2_score]);
    const b_total_score = getTotalScore([
      scores.b1_score,
      scores.b2_score,
      scores.b3_score,
      scores.b4_score,
      scores.b5_score
    ]);
    const c_total_score = getTotalScore([scores.c1_score, scores.c2_score]);
    const d_total_score = getTotalScore([scores.d1_score, scores.d2_score]);
    const I_total_score = a_total_score + b_total_score + c_total_score + d_total_score;
    const II_total_score = getTotalScore([scores.hinhthuc1_score, scores.hinhthuc2_score]);
    const summary_score = I_total_score + II_total_score;
    return {
      I_total_score,
      II_total_score,
      summary_score,
      a_total_score,
      b_total_score,
      c_total_score,
      d_total_score
    };
  }, [scores]);

  const viewData = useMemo(() => {
    return [
      {
        key: 'I',
        stt: 'I',
        isTitle: true,
        titleComment: 'Nội dung',
        standard_score: 90,
        score: calculatedScore.I_total_score
      },
      {
        key: 'Ia',
        stt: 'a',
        isTitle: false,
        titleComment: 'Tính mới',
        standard_score: 20,
        isSubTitle: true,
        rowSpan: 3,
        score: calculatedScore.a_total_score
      },
      {
        key: 'a1',
        stt: 'a',
        isTitle: false,
        titleComment: initiativeComment.a1,
        standard_score: 10,
        rowSpan: 0,
        score: scores.a1_score
      },
      {
        key: 'a2',
        stt: 'a',
        isTitle: false,
        titleComment: initiativeComment.a2,
        standard_score: 10,
        rowSpan: 0,
        score: scores.a2_score
      },
      {
        key: 'Ib',
        stt: 'b',
        isTitle: false,
        titleComment: 'Tính khoa học',
        standard_score: 25,
        isSubTitle: true,
        rowSpan: 6,
        score: calculatedScore.b_total_score
      },
      {
        key: 'b1',
        stt: 'b',
        isTitle: false,
        titleComment: initiativeComment.b1,
        standard_score: 3,
        rowSpan: 0,
        score: scores.b1_score
      },
      {
        key: 'b2',
        stt: 'b',
        isTitle: false,
        titleComment: initiativeComment.b2,
        standard_score: 7,
        rowSpan: 0,
        score: scores.b2_score
      },
      {
        key: 'b3',
        stt: 'b',
        isTitle: false,
        titleComment: initiativeComment.b3,
        standard_score: 6,
        rowSpan: 0,
        score: scores.b3_score
      },
      {
        key: 'b4',
        stt: 'b',
        isTitle: false,
        titleComment: initiativeComment.b4,
        standard_score: 6,
        rowSpan: 0,
        score: scores.b4_score
      },
      {
        key: 'b5',
        stt: 'b',
        isTitle: false,
        titleComment: initiativeComment.b5,
        standard_score: 3,
        rowSpan: 0,
        score: scores.b5_score
      },
      {
        key: 'Ic',
        stt: 'c',
        isTitle: false,
        titleComment: 'Tính thực tiễn',
        standard_score: 20,
        isSubTitle: true,
        rowSpan: 3,
        score: calculatedScore.c_total_score
      },
      {
        key: 'c1',
        stt: 'c',
        isTitle: false,
        titleComment: initiativeComment.c1,
        standard_score: 10,
        rowSpan: 0,
        score: scores.c1_score
      },
      {
        key: 'c2',
        stt: 'c',
        isTitle: false,
        titleComment: initiativeComment.c2,
        standard_score: 10,
        rowSpan: 0,
        score: scores.c2_score
      },
      {
        key: 'Id',
        stt: 'd',
        isTitle: false,
        titleComment: 'Tính hiệu quả',
        standard_score: 25,
        isSubTitle: true,
        rowSpan: 3,
        score: calculatedScore.d_total_score
      },
      {
        key: 'd1',
        stt: 'd',
        isTitle: false,
        titleComment: initiativeComment.d1,
        standard_score: 15,
        rowSpan: 0,
        score: scores.d1_score
      },
      {
        key: 'd2',
        stt: 'd',
        isTitle: false,
        titleComment: initiativeComment.d2,
        standard_score: 10,
        rowSpan: 0,
        score: scores.d2_score
      },
      {
        key: 'II',
        stt: 'II',
        isTitle: true,
        titleComment: 'Hình thức',
        standard_score: 10,
        score: calculatedScore.II_total_score
      },
      {
        key: 'hinhthuc1',
        stt: '',
        isTitle: false,
        titleComment: initiativeComment.hinhthuc1,
        standard_score: 5,
        rowSpan: 2,
        score: scores.hinhthuc1_score
      },
      {
        key: 'hinhthuc2',
        stt: '',
        isTitle: false,
        titleComment: initiativeComment.hinhthuc2,
        standard_score: 5,
        rowSpan: 0,
        score: scores.hinhthuc2_score
      },
      {
        key: 'summary',
        stt: '',
        isTitle: false,
        isSummary: true,
        titleComment: 'TỔNG CỘNG',
        standard_score: 100,
        score: calculatedScore.summary_score
      },
      {
        key: 'confirm',
        stt: '',
        isConfirm: true,
        titleComment: 'ĐÁNH GIÁ'
      }
    ];
  }, [calculatedScore, scores]);

  const columns: ColumnsType<any> = [
    {
      title: 'Mục',
      dataIndex: 'stt',
      key: 'stt',
      width: 60,
      align: 'center',
      onCell: (record: any) => {
        if (record.isSummary || record.isConfirm) {
          return {
            colSpan: 0
          };
        }
        if (record.hasOwnProperty('rowSpan')) {
          return {
            rowSpan: record.rowSpan
          };
        }
        return {};
      },
      render: (record) => <span style={{ fontWeight: 'bold' }}>{record}</span>
    },
    {
      title: 'Nhận xét đề tài',
      key: 'comment',
      dataIndex: 'titleComment',
      onCell: (record: any) => {
        if (record.isSummary) {
          return {
            colSpan: 2
          };
        }
        if (record.isConfirm) {
          return {
            colSpan: 2
          };
        }
        return {};
      },
      render: (_, record: any) => {
        if (record.isSubTitle || record.isConfirm) {
          return <span style={{ fontWeight: 'bold' }}>{record.titleComment}</span>;
        }
        return record.titleComment;
      }
    },
    {
      title: 'Điểm quy định',
      dataIndex: 'standard_score',
      key: 'standard_score',
      width: 140,
      align: 'center',
      onCell: (record: any) => {
        if (record.isConfirm) {
          return {
            colSpan: 2
          };
        }
        return {};
      },
      render: (_, record: any) => {
        if (record.isSubTitle || record.isTitle) {
          return <span style={{ fontWeight: 'bold' }}>{record.standard_score}</span>;
        }
        if (record.isConfirm) {
          return (
            <StyledConfirm>
              <div>
                <StyledCheckbox checked={confirm === true} />
                <span style={{ marginLeft: '5px' }}>Đạt</span>
              </div>

              <div>
                <StyledCheckbox checked={confirm === false} />
                <span style={{ marginLeft: '5px' }}>Không Đạt</span>
              </div>
            </StyledConfirm>
          );
        }
        return record.standard_score;
      }
    },
    {
      title: 'Điểm chấm HĐ',
      key: 'score',
      width: 140,
      align: 'center',
      onCell: (record: any) => {
        if (record.isConfirm) {
          return {
            colSpan: 0
          };
        }
        return {};
      },
      render: (record: any) => {
        if (record.isTitle || record.isSummary || record.isSubTitle) {
          return record.score;
        }
        const currentScore = scores[`${record.key}_score`];

        return currentScore;
      }
    }
  ];

  const handleSuccess = useCallback(
    (file?: File) => {
      if (file) {
        setGeneratedFile(file);
        setOpenGenerate(false);
        setOpenSignFile(true);
        return;
      }

      onClose();
    },
    [onClose]
  );

  if (loading) {
    return (
      <FullScreenModal visible={open} onCancel={onClose}>
        <Spin spinning={loading} tip="Đang xử lý dữ liệu...">
          <div style={{ width: '100%', height: '500px' }}></div>
        </Spin>
      </FullScreenModal>
    );
  }

  return (
    <>
      {openGenerate && !loading && (
        <ModalGenerate
          open={open}
          onClose={onClose}
          onSuccess={handleSuccess}
          reviewerComment={reviewerComment}
          columns={columns}
          viewData={viewData}
          data={initiative}
          isCv1={isCv1}
        />
      )}

      {openSignFile && generatedFile && (
        <ModalViewPDFSignExport open={openSignFile} file={generatedFile} onClose={onClose} />
      )}
    </>
  );
};

export default ModalGenerateAndOpenForSign;
